import React from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useWindowResize } from 'hooks/useWindowResize';
import { Typography } from 'lib/components';
import classes from './index.module.scss';

/**
 * Props interface for the DatePicker component
 * Extends ReactDatePicker props with custom properties
 * Omits some props that we want to control directly
 */
interface Props extends Omit<ReactDatePickerProps, 'onChange' | 'selected' | 'value' | 'minDate' | 'maxDate'> {
	/** Label text for the date picker */
	label: string;
	/** Currently selected date value */
	value: Date | null;
	/** Minimum selectable date */
	minDate?: Date;
	/** Maximum selectable date */
	maxDate?: Date;
	/** Callback function when date changes */
	setValue: (date: Date) => void;
}

/**
 * DatePicker component
 * Renders a styled date picker with label
 * Wraps the react-datepicker library with custom styling
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const DatePicker = ({ label, value, minDate, maxDate, setValue, ...rest }: Props) => {
	// Responsive design helpers
	const { isSmallerDevice } = useWindowResize();

	return (
		<div className={classes['date-picker-group']}>
			{/* Date picker label */}
			<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
				{label}:
			</Typography>
			{/* React DatePicker component with custom styling */}
			<ReactDatePicker
				selected={value}
				onChange={(date: Date) => setValue(date)}
				minDate={minDate}
				maxDate={maxDate}
				className={classes['react-datepicker']}
				{...rest}
			/>
		</div>
	);
};

export default DatePicker;
